// ComponentsPercentSales.tsx
import React from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { AccountEntry, CategoryOption, Project } from '../../types';
import { 
  calculateCategoryTotal, 
  calculateTTM, 
  calculateTTMForPeriodType,
  calculateAccountsByPeriod,
  getQuarter,
  getYear 
} from '../../lib/financial/calculations';

interface HeaderProps {
  title: string;
  isOpen: boolean;
  onClick: () => void;
  colSpan?: number;
}

export const SectionHeader: React.FC<HeaderProps> = ({ 
  title, 
  isOpen, 
  onClick, 
  colSpan
}) => (
  <tr className="bg-gray-50 cursor-pointer hover:bg-gray-100" onClick={onClick}>
    <td className="sticky left-0 z-10 bg-gray-50 px-6 py-3 text-sm font-semibold text-black border-r border-gray-200">
      <div className="flex items-center">
        {isOpen ? (
          <ChevronDown className="h-4 w-4 mr-2" />
        ) : (
          <ChevronRight className="h-4 w-4 mr-2" />
        )}
        <span>{title}</span>
      </div>
    </td>
    <td className="px-6 py-2 text-sm text-gray-600 text-center" />
    <td colSpan={colSpan} />
  </tr>
);

interface AccountRowProps {
  account: AccountEntry;
  periods: string[];
  viewType: 'monthly' | 'quarterly' | 'yearly';
  isRevenue: boolean;
  isContraRevenue: boolean;
  netRevenue: (period: string) => number;
}

export const AccountRow: React.FC<AccountRowProps> = ({
  account,
  periods,
  viewType,
  isRevenue,
  isContraRevenue,
  netRevenue
}) => {
  return (
    <tr className="bg-gray-50">
      <td className="pl-12 py-2 text-sm text-gray-600">
        {account.name}
      </td>
      <td className="px-6 py-2 text-sm text-gray-600">
        {account.accountNumber}
      </td>
      {periods.map(period => {
        let amount = 0;
        if (viewType === 'monthly') {
          amount = account.month === period ? 
            (isRevenue ? 
              (account.credit - account.debit)
              : isContraRevenue ? 
                -(account.credit - account.debit)
                : (account.debit - account.credit))
            : 0;
        } else if (viewType === 'quarterly') {
          const matchingAccounts = calculateAccountsByPeriod(
            [account], 
            account.category || '', 
            period, 
            'quarterly'
          );
          amount = matchingAccounts.reduce((sum, acc) => 
            sum + (isRevenue ? 
              (acc.credit - acc.debit)
              : isContraRevenue ? 
                -(acc.credit - acc.debit)
                : (acc.debit - acc.credit))
          , 0);
        } else { // yearly
          const matchingAccounts = calculateAccountsByPeriod(
            [account], 
            account.category || '', 
            period, 
            'yearly'
          );
          amount = matchingAccounts.reduce((sum, acc) => 
            sum + (isRevenue ? 
              (acc.credit - acc.debit)
              : isContraRevenue ? 
                -(acc.credit - acc.debit)
                : (acc.debit - acc.credit))
          , 0);
        }

        const periodNetRevenue = netRevenue(period);
        const percentage = periodNetRevenue !== 0 ? (amount / periodNetRevenue) * 100 : 0;

        return (
          <td key={period} className="px-6 py-2 text-sm text-gray-600 text-right">
            {percentage.toFixed(1)}%
          </td>
        );
      })}
    </tr>
  );
};

interface SectionTotalProps {
  title: string;
  periods: string[];
  total: (period: string) => number;
  netRevenue: (period: string) => number;
  ttmTotal?: number;
  ttmNetRevenue?: number;
  className?: string;
}

export const SectionTotal: React.FC<SectionTotalProps> = ({
  title,
  periods,
  total,
  netRevenue,
  ttmTotal,
  ttmNetRevenue,
  className = "bg-gray-200"
}) => {
  return (
    <tr className={className}>
      <td className="sticky left-0 z-10 bg-inherit px-6 py-3 text-sm font-semibold text-black border-r border-gray-200">
        {title}
      </td>
      <td className="px-6 py-2 text-sm text-gray-600 text-center" />
      {periods.map(period => {
        const totalAmount = total(period);
        const periodNetRevenue = netRevenue(period);
        const percentage = periodNetRevenue !== 0 ? (totalAmount / periodNetRevenue) * 100 : 0;
        return (
          <td key={period} className="px-6 py-3 text-sm font-semibold text-gray-900 text-right">
            {percentage.toFixed(1)}%
          </td>
        );
      })}
      {ttmTotal !== undefined && ttmNetRevenue !== undefined && (
        <td className="px-6 py-3 text-sm font-semibold text-gray-900 text-right">
          {(ttmNetRevenue !== 0 ? (ttmTotal / ttmNetRevenue) * 100 : 0).toFixed(1)}%
        </td>
      )}
    </tr>
  );
};

interface CategoryRowProps {
  category: string;
  periods: string[];
  accounts: AccountEntry[];
  viewType: 'monthly' | 'quarterly' | 'yearly';
  allMonths: string[];
  isRevenue: boolean;
  isContraRevenue: boolean;
  templateCategories?: CategoryOption[];
  isExpanded: boolean;
  onToggle: () => void;
  netRevenue: (period: string) => number;
  ttmNetRevenue?: number;
  selectedProject?: Project | null;
}

export const CategoryRow: React.FC<CategoryRowProps> = ({
  category,
  periods,
  accounts,
  viewType,
  allMonths,
  isRevenue,
  isContraRevenue,
  templateCategories,
  isExpanded,
  onToggle,
  netRevenue,
  ttmNetRevenue,
  selectedProject
}) => {
  // Get the display name from template categories if available
  const getCategoryDisplayName = (categoryValue: string) => {
    if (!templateCategories) return categoryValue;
    
    const templateCategory = templateCategories.find(cat => cat.value === categoryValue);
    return templateCategory?.label || categoryValue;
  };
  
  // Get unique accounts but preserve all monthly entries
  const categoryAccounts = React.useMemo(() => {
    const accountMap = new Map<string, AccountEntry[]>();
    
    accounts
      .filter(acc => acc.category === category)
      .forEach(acc => {
        if (!accountMap.has(acc.accountNumber)) {
          accountMap.set(acc.accountNumber, []);
        }
        accountMap.get(acc.accountNumber)?.push(acc);
      });

    return Array.from(accountMap.entries()).map(([accountNumber, entries]) => ({
      accountNumber,
      name: entries[0].name,
      entries
    }));
  }, [accounts, category]);

  // Check if the category has any non-zero values across all periods
  const hasNonZeroValues = React.useMemo(() => {
    return periods.some(period => {
      const total = calculateCategoryTotal(
        accounts,
        category,
        period,
        viewType,
        isRevenue,
        isContraRevenue
      );
      return Math.abs(total) > 0;
    });
  }, [accounts, category, periods, viewType, isRevenue, isContraRevenue]);

  // Don't render anything if all values are zero
  if (!hasNonZeroValues) {
    return null;
  }

  return (
    <>
      <tr className="cursor-pointer hover:bg-gray-50" onClick={onToggle}>
        <td className="sticky left-0 z-10 bg-gray-50 px-6 py-3 text-sm text-black border-r border-gray-200">
          <div className="flex items-center">
            {isExpanded ? (
              <ChevronDown className="h-4 w-4 mr-2" />
            ) : (
              <ChevronRight className="h-4 w-4 mr-2" />
            )}
            {getCategoryDisplayName(category)}
          </div>
        </td>
        <td className="px-6 py-2 text-sm text-gray-600 text-center" />
        
        {periods.map(period => {
          const amount = calculateCategoryTotal(
            accounts,
            category,
            period,
            viewType,
            isRevenue,
            isContraRevenue
          );

          const periodNetRevenue = netRevenue(period);
          const percentage = periodNetRevenue !== 0 ? (amount / periodNetRevenue) * 100 : 0;

          return (
            <td key={period} className="px-6 py-2 text-sm text-gray-900 text-right">
              {percentage.toFixed(1)}%
            </td>
          );
        })}
        {viewType === 'yearly' && ttmNetRevenue !== undefined && (
          <td className="px-6 py-2 text-sm text-gray-900 text-right">
            {(() => {
              const projectPeriodType = selectedProject?.period_type || 'monthly';
              const ttmAmount = calculateTTMForPeriodType(
                accounts,
                category,
                allMonths,
                isRevenue,
                isContraRevenue,
                projectPeriodType
              );
              return (ttmNetRevenue !== 0 ? (ttmAmount / ttmNetRevenue) * 100 : 0).toFixed(1) + '%';
            })()}
          </td>
        )}
      </tr>
      {isExpanded && categoryAccounts.map(({accountNumber, name, entries}) => (
        <tr key={accountNumber} className="bg-gray-50">
          <td className="sticky left-0 z-10 bg-gray-50 pl-12 py-2 text-sm text-gray-600 border-r border-gray-200">
            {name}
          </td>
          <td className="px-6 py-2 text-sm text-gray-600 text-center">
            {accountNumber}
          </td>
          {periods.map(period => {
            let amount = 0;
            if (viewType === 'monthly') {
              const periodEntry = entries.find(entry => entry.month === period);
              amount = periodEntry ? 
                (isRevenue ? 
                  (periodEntry.credit - periodEntry.debit)
                  : isContraRevenue ? 
                    -(periodEntry.credit - periodEntry.debit)
                    : (periodEntry.debit - periodEntry.credit))
                : 0;
            } else if (viewType === 'quarterly') {
              const matchingAccounts = calculateAccountsByPeriod(
                entries, 
                category, 
                period, 
                'quarterly'
              );
              amount = matchingAccounts.reduce((sum, entry) => 
                sum + (isRevenue ? 
                  (entry.credit - entry.debit)
                  : isContraRevenue ? 
                    -(entry.credit - entry.debit)
                    : (entry.debit - entry.credit))
              , 0);
            } else {
              const matchingAccounts = calculateAccountsByPeriod(
                entries, 
                category, 
                period, 
                'yearly'
              );
              amount = matchingAccounts.reduce((sum, entry) => 
                sum + (isRevenue ? 
                  (entry.credit - entry.debit)
                  : isContraRevenue ? 
                    -(entry.credit - entry.debit)
                    : (entry.debit - entry.credit))
              , 0);
            }

            const periodNetRevenue = netRevenue(period);
            const percentage = periodNetRevenue !== 0 ? (amount / periodNetRevenue) * 100 : 0;

            return (
              <td key={period} className="px-6 py-2 text-sm text-gray-600 text-right">
                {percentage.toFixed(1)}%
              </td>
            );
          })}
          {viewType === 'yearly' && ttmNetRevenue !== undefined && (
            <td className="px-6 py-2 text-sm text-gray-600 text-right">
              {(() => {
                const projectPeriodType = selectedProject?.period_type || 'monthly';
                
                // Apply period-aware lookback for individual account entries
                let lookbackPeriods: number;
                switch (projectPeriodType) {
                  case 'quarterly':
                    lookbackPeriods = 4; // Last 4 quarters
                    break;
                  case 'annual':
                    lookbackPeriods = 1; // Last 1 year
                    break;
                  default: // monthly
                    lookbackPeriods = 12; // Last 12 months
                    break;
                }
                
                const relevantMonths = allMonths.slice(-lookbackPeriods);
                const ttmAmount = entries
                  .filter(entry => relevantMonths.includes(entry.month))
                  .reduce((sum, entry) => 
                    sum + (isRevenue ? 
                      (entry.credit - entry.debit)
                      : isContraRevenue ? 
                        -(entry.credit - entry.debit)
                        : (entry.debit - entry.credit))
                  , 0);
                return (ttmNetRevenue !== 0 ? (ttmAmount / ttmNetRevenue) * 100 : 0).toFixed(1) + '%';
              })()}
            </td>
          )}
        </tr>
      ))}
    </>
  );
};